import { useMemo, useRef, useState } from 'react';
import { Column } from 'react-table';
import {
	Button,
	Card,
	Col,
	Container,
	Form,
	Modal,
	Row,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import ServerPaginatedTable from 'src/components/ServerPaginatedTable/ServerPaginatedTable';
import TableSkeleton from 'src/components/TableSkeleton/TableSkeleton';
import { useApiClient } from 'src/hooks/useApiClient';
import {
	serializeProgress,
	useConfiguratorState,
} from 'src/components/CarConfiguratorMotornet/store';
import {
	useConfigurationFetcher,
	useDeleteConfiguration,
	useSavedConfigurations,
} from 'src/components/CarConfiguratorMotornet/savedConfigurations/api';
import { probeAvailability } from 'src/components/CarConfiguratorMotornet/savedConfigurations/availability';
import { useRestoreStore } from 'src/components/CarConfiguratorMotornet/savedConfigurations/restoreStore';
import { UnavailableConfigurationModal } from 'src/components/CarConfiguratorMotornet/savedConfigurations/UnavailableConfigurationModal';
import { useConfiguratorTranslations } from 'src/components/CarConfiguratorMotornet/translations';
import type {
	SavedCarConfiguration,
	SavedConfigurationRow,
	UnavailableReason,
} from 'src/components/CarConfiguratorMotornet/savedConfigurations/types';
import toaster from 'src/utils/toaster';
import { createColumnsData } from './columnsData';
import './MyCarConfigurationsPage.scss';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;

const similarParams = (listing: SavedConfigurationRow['listing']) =>
	new URLSearchParams({
		step: 'model-selector',
		makeCode: listing.makeCode,
		make: listing.makeLabel,
		modelCode: listing.modelCode,
		model: listing.modelLabel,
	});

const stockSelectorParams = (listing: SavedConfigurationRow['listing']) =>
	new URLSearchParams({
		step: 'customizer',
		section: 'engine',
		makeCode: listing.makeCode,
		make: listing.makeLabel,
		modelCode: listing.modelCode,
		model: listing.modelLabel,
	});

const MyCarConfigurationsPage = () => {
	const { t } = useTranslation();
	const tr = useConfiguratorTranslations();
	const { apiClient } = useApiClient();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const fetchConfiguration = useConfigurationFetcher();
	const deleteConfiguration = useDeleteConfiguration();

	const [searchInput, setSearchInput] = useState('');
	const [search, setSearch] = useState('');
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [busyId, setBusyId] = useState<string | null>(null);
	const [pendingDelete, setPendingDelete] =
		useState<SavedConfigurationRow | null>(null);
	const [unavailable, setUnavailable] = useState<{
		reason: UnavailableReason;
		record: SavedCarConfiguration | SavedConfigurationRow;
	} | null>(null);

	const handleSearch = () => {
		setSearch(searchInput.trim());
		setPage(1);
	};

	const handleSearchReset = () => {
		setSearchInput('');
		setSearch('');
		setPage(1);
	};

	const { data, isPending, isFetching, error } = useSavedConfigurations(
		search,
		page,
		pageSize
	);

	const items = useMemo(() => data?.items ?? [], [data]);
	const lastKnownTotal = useRef(0);
	const dataFilter = useRef(search);
	if (data) {
		lastKnownTotal.current = data.total;
		dataFilter.current = search;
	}
	const totalCount = data?.total ?? lastKnownTotal.current;
	const isFilterChanging = search !== dataFilter.current;

	const opening = useRef(false);
	const handleOpen = async (row: SavedConfigurationRow) => {
		if (opening.current) return;

		if (!row.available && row.unavailableReason) {
			setUnavailable({ reason: row.unavailableReason, record: row });
			return;
		}

		opening.current = true;
		setBusyId(row.id);
		let record: SavedCarConfiguration;
		let reason: UnavailableReason | null;
		try {
			record = await fetchConfiguration(row.id);
			reason = await probeAvailability({ queryClient, apiClient, record });
		} catch (err) {
			console.error('verifica configurazione fallita', err);
			toaster.error(t('error_loading_data'));
			return;
		} finally {
			opening.current = false;
			setBusyId(null);
		}

		if (reason) {
			setUnavailable({ reason, record });
			return;
		}

		useConfiguratorState.getState().reset();
		useRestoreStore.getState().request(record);
		navigate(
			`/car-configurator?${
				record.vehicleType === 'stock'
					? stockSelectorParams(record.listing)
					: serializeProgress(record.payload.progress)
			}`
		);
	};

	const confirmDelete = () => {
		const target = pendingDelete;
		if (!target) return;
		setPendingDelete(null);
		setBusyId(target.id);
		deleteConfiguration
			.mutateAsync(target.id)
			.then(() => toaster.success(tr.savedConfigDeleted))
			.catch((err) => {
				console.error('eliminazione configurazione fallita', err);
				toaster.error(tr.savedConfigDeleteError);
			})
			.finally(() => setBusyId(null));
	};

	const columns = useMemo(
		() =>
			createColumnsData(
				handleOpen,
				setPendingDelete,
				busyId
			) as Column<object>[],
		[busyId] // eslint-disable-line react-hooks/exhaustive-deps
	);

	const searchLabel = t('my_car_configurations_search', {
		defaultValue: 'Cerca per marca, modello o allestimento',
	});

	return (
		<Container fluid className="my-car-configurations-page">
			<Row>
				<Col md={6} className="display-6">
					{t('my_car_configurations', {
						defaultValue: 'Le mie configurazioni',
					})}
				</Col>
			</Row>

			<Card className="mt-4">
				<Card.Body className="py-3 filter-bar">
					<Form
						className="d-flex align-items-center"
						onSubmit={(event) => {
							event.preventDefault();
							handleSearch();
						}}
					>
						<Form.Control
							className="flex-grow-1"
							type="search"
							value={searchInput}
							placeholder={searchLabel}
							aria-label={searchLabel}
							onChange={(event) => {
								const value = event.target.value;
								setSearchInput(value);
								if (value === '') {
									setSearch('');
									setPage(1);
								}
							}}
						/>
					</Form>
					<div className="d-flex align-items-center justify-content-end gap-2">
						{(searchInput !== '' || search !== '') && (
							<Button
								type="button"
								className="filter-reset-btn text-nowrap"
								onClick={handleSearchReset}
							>
								{t('reset_filters')}
							</Button>
						)}
						<Button
							type="button"
							variant="outline-secondary"
							className="text-nowrap"
							onClick={handleSearch}
						>
							{t('motornet_backfill_search', 'Cerca')}
						</Button>
					</div>
				</Card.Body>
			</Card>

			{error ? (
				<Card className="my-4">
					<Card.Body className="text-center text-danger py-5">
						{error instanceof Error && error.message
							? error.message
							: t('error_loading_data')}
					</Card.Body>
				</Card>
			) : isFilterChanging ? (
				<TableSkeleton cols={columns.length} rows={pageSize} />
			) : (
				<ServerPaginatedTable
					key={`${search}-${pageSize}`}
					columns={columns}
					data={items}
					loading={isPending || isFetching}
					error={error}
					totalCount={totalCount}
					pageSize={pageSize}
					showPageSelector
					pageSizeOptions={PAGE_SIZE_OPTIONS}
					initialPage={page}
					onPageSizeChange={(newSize: number) => {
						setPageSize(newSize);
						setPage(1);
					}}
					loadFn={(skip: number, limit: number) =>
						setPage(Math.floor(skip / limit) + 1)
					}
					dblClickCallback={(item: SavedConfigurationRow) => handleOpen(item)}
				/>
			)}

			{pendingDelete && (
				<Modal show centered onHide={() => setPendingDelete(null)}>
					<Modal.Header closeButton>
						<Modal.Title>{tr.savedConfigDeleteTitle}</Modal.Title>
					</Modal.Header>
					<Modal.Body>{tr.savedConfigDeleteBody}</Modal.Body>
					<Modal.Footer>
						<Button variant="danger" onClick={confirmDelete}>
							{tr.savedConfigDeleteConfirm}
						</Button>
						<Button
							variant="outline-secondary"
							onClick={() => setPendingDelete(null)}
						>
							{tr.savedConfigDeleteCancel}
						</Button>
					</Modal.Footer>
				</Modal>
			)}

			{unavailable && (
				<UnavailableConfigurationModal
					reason={unavailable.reason}
					record={unavailable.record}
					onClose={() => setUnavailable(null)}
					onConfigureSimilar={(listing) => {
						setUnavailable(null);
						useConfiguratorState.getState().reset();
						navigate(`/car-configurator?${similarParams(listing)}`);
					}}
				/>
			)}
		</Container>
	);
};

export default MyCarConfigurationsPage;
